import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/react';
import { Link, useSearchParams } from 'react-router-dom';
import ConfidenceBadge from '../components/ConfidenceBadge';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.horizontl.io';

const STATUS_LABELS = {
  pending: 'No feedback',
  draft_saved: 'No feedback',
  sending: 'Sending',
  delivery_failed: 'Delivery issue',
  approved_unchanged: 'Accepted',
  edited_and_sent: 'Accepted',
  rejected: 'Rejected',
  not_applicable: 'Autonomous',
};

const REJECTION_OPTIONS = [
  ['factually_incorrect', 'Factually incorrect'],
  ['unsupported_by_documentation', 'Unsupported by documentation'],
  ['missed_relevant_documentation', 'Missed relevant documentation'],
  ['misunderstood_issue', 'Misunderstood the issue'],
  ['irrelevant_troubleshooting', 'Irrelevant troubleshooting'],
  ['missing_important_steps', 'Missing important steps'],
  ['unnecessary_questions', 'Asked unnecessary questions'],
  ['unsafe_recommendation', 'Unsafe or risky recommendation'],
  ['wrong_tone_or_context', 'Wrong tone or customer context'],
  ['duplicate_or_addressed', 'Duplicate or already addressed'],
  ['other', 'Other'],
];

const DETAIL_REQUIRED_REASONS = new Set(['factually_incorrect', 'unsafe_recommendation', 'other']);

const statusTone = ticket => {
  if (ticket.delivery_status === 'failed' || ticket.review_status === 'delivery_failed') return 'danger';
  if (['approved_unchanged', 'edited_and_sent', 'not_applicable'].includes(ticket.review_status)) return 'success';
  if (ticket.review_status === 'rejected') return 'neutral';
  return 'warning';
};

const canonical = value => (value || '')
  .replace(/\r\n/g, '\n')
  .split('\n')
  .map(line => line.trimEnd())
  .join('\n')
  .trim();

const formatDateTime = value => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
};

const formatDuration = seconds => {
  if (seconds == null) return 'Not yet measured';
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${total % 60}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return `${hours}h ${remainingMinutes}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const draftLabel = ticket => (
  Number(ticket?.response_revision || 1) > 1 ? 'Amended draft' : 'Initial draft'
);

const errorMessage = detail => ({
  response_revision_conflict: 'This response was changed in another session. Reload the ticket before continuing.',
  ticket_review_already_completed: 'This ticket has already been reviewed.',
  ticket_response_already_delivered: 'A customer-visible response has already been delivered.',
  ticketing_delivery_failed: 'Jira did not confirm delivery. The ticket remains in Delivery issues and will not be sent again automatically.',
  rejection_reason_required: 'Select at least one rejection reason.',
  rejection_detail_required: 'Add a short explanation for this rejection reason.',
  invalid_rejection_reason: 'One selected rejection reason is not supported. Refresh and try again.',
  ticket_not_found_in_live_project: 'This ticket is not part of the currently published Jira project.',
}[detail] || detail || 'The review action could not be completed.');

export default function ReviewQueue() {
  const { getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const [filter, setFilter] = useState(
    ['open', 'completed', 'all'].includes(requestedView) ? requestedView : 'all',
  );
  const [queue, setQueue] = useState({ items: [], counts: {}, project: null });
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editor, setEditor] = useState('');
  const [feedbackChoice, setFeedbackChoice] = useState('');
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [rejectionDetail, setRejectionDetail] = useState('');

  const loadQueue = useCallback(async ({ preserveMessage = false } = {}) => {
    setLoading(true);
    if (!preserveMessage) {
      setError('');
      setSuccess('');
    }
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_URL}/api/review-queue?status=${encodeURIComponent(filter)}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(data.detail));
      setQueue(data);
    } catch (caught) {
      setError(caught.message || 'The review queue could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [filter, getToken]);

  const loadMetrics = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/review-queue/metrics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) setMetrics(data);
    } catch (_) {
      // Queue actions remain available if the aggregate metrics request fails.
    }
  }, [getToken]);

  useEffect(() => { loadQueue(); loadMetrics(); }, [loadMetrics, loadQueue]);

  const requestedTicketId = searchParams.get('ticket');
  const selectedTicket = useMemo(
    () => queue.items.find(ticket => ticket.id === requestedTicketId) || queue.items[0] || null,
    [queue.items, requestedTicketId],
  );

  useEffect(() => {
    if (selectedTicket && selectedTicket.id !== requestedTicketId) {
      setSearchParams({ view: filter, ticket: selectedTicket.id }, { replace: true });
    }
  }, [filter, requestedTicketId, selectedTicket, setSearchParams]);

  useEffect(() => {
    setEditor(selectedTicket?.draft_response || '');
    setFeedbackChoice('');
    setRejectionReasons([]);
    setRejectionDetail('');
  }, [selectedTicket?.id, selectedTicket?.response_revision, selectedTicket?.draft_response]);

  const dirty = Boolean(selectedTicket)
    && canonical(editor) !== canonical(selectedTicket.draft_response);
  const changedFromDana = Boolean(selectedTicket)
    && canonical(editor) !== canonical(selectedTicket.original_draft_response);
  const completed = selectedTicket
    && ['approved_unchanged', 'edited_and_sent', 'rejected', 'not_applicable'].includes(selectedTicket.review_status);

  const updateSelectedTicket = updated => {
    setQueue(current => ({
      ...current,
      items: current.items.map(ticket => ticket.id === updated.id ? updated : ticket),
    }));
  };

  const saveDraft = async () => {
    if (!selectedTicket || !canonical(editor) || !dirty) return;
    setBusy('save'); setError(''); setSuccess('');
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/review-queue/${selectedTicket.id}/draft`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: editor, expected_revision: selectedTicket.response_revision }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(data.detail));
      updateSelectedTicket(data);
      setEditor(data.draft_response);
      setSuccess(`Draft v${data.response_revision} saved. ${data.change_percentage}% changed from Dana’s response.`);
    } catch (caught) {
      setError(caught.message || 'The draft could not be saved.');
    } finally {
      setBusy('');
    }
  };

  const submitFeedback = async () => {
    if (!selectedTicket || !canonical(editor)) return;
    if (!feedbackChoice) {
      setError('Select Accepted or Rejected before completing this review.');
      return;
    }
    const decision = feedbackChoice === 'rejected' ? 'reject' : 'send';
    if (decision === 'reject' && !rejectionReasons.length) {
      setError('Select at least one rejection reason.');
      return;
    }
    const rejectionDetailRequired = rejectionReasons.some(reason => DETAIL_REQUIRED_REASONS.has(reason));
    if (decision === 'reject' && rejectionDetailRequired && canonical(rejectionDetail).length < 3) {
      setError('Add a short explanation for the selected rejection reason.');
      return;
    }
    const actionLabel = changedFromDana ? 'send the edited response' : 'send Dana’s response unchanged';
    if (decision === 'send' && !window.confirm(`Confirm you want to ${actionLabel} publicly in Jira?`)) return;

    setBusy(decision); setError(''); setSuccess('');
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/review-queue/${selectedTicket.id}/decision`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision,
          response: editor,
          expected_revision: selectedTicket.response_revision,
          rejection_reasons: decision === 'reject' ? rejectionReasons : [],
          reason_detail: decision === 'reject' ? rejectionDetail : null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(data.detail));
      const completedMessage = decision === 'reject'
        ? 'Draft rejected. Nothing was sent to Jira.'
        : `Draft accepted and sent to Jira. ${data.change_percentage}% changed from Dana’s original.`;
      setFeedbackChoice('');
      await Promise.all([loadQueue({ preserveMessage: true }), loadMetrics()]);
      setSuccess(completedMessage);
    } catch (caught) {
      setError(caught.message || 'The review decision could not be completed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="page-stack review-queue-page">
      <section className="queue-summary" aria-label="Review queue summary">
        <article><span>No feedback</span><strong>{metrics?.no_feedback ?? Math.max((queue.counts.open || 0) - (queue.counts.delivery_issues || 0), 0)}</strong></article>
        <article><span>Accepted</span><strong>{metrics?.accepted ?? ((metrics?.sent_unchanged || 0) + (metrics?.edited_and_sent || 0))}</strong></article>
        <article><span>Rejected</span><strong>{metrics?.rejected || 0}</strong></article>
        <article title="Accepted customer responses that Jira failed to confirm"><span>Delivery failures</span><strong>{metrics?.delivery_failures ?? (queue.counts.delivery_issues || 0)}</strong></article>
      </section>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner"><strong>{success}</strong></div>}

      <div className="review-workspace">
        <section className="panel queue-list-panel">
          <div className="queue-list-header">
            <div><h2>{queue.project?.key || 'Live'} queue</h2><p>{queue.items.length} shown</p></div>
            <label><span className="sr-only">Filter queue</span><select value={filter} onChange={event => { setFilter(event.target.value); setSearchParams({ view: event.target.value }); }}><option value="all">All tickets</option><option value="open">Needs attention</option><option value="completed">Completed</option></select></label>
          </div>
          <div className="queue-ticket-list">
            {queue.items.map(ticket => (
              <button className={`queue-ticket ${selectedTicket?.id === ticket.id ? 'selected' : ''}`} key={ticket.id} type="button" onClick={() => { setError(''); setSuccess(''); setSearchParams({ view: filter, ticket: ticket.id }); }}>
                <span className="queue-ticket-topline"><strong>{ticket.ticket_key}</strong><span className={`queue-state ${statusTone(ticket)}`}>{STATUS_LABELS[ticket.review_status] || 'Needs review'}</span></span>
                <span className="queue-ticket-summary">{ticket.summary || 'No summary provided'}</span>
                <span className="queue-ticket-meta"><ConfidenceBadge ticket={ticket} /><time>{formatDateTime(ticket.updated_at)}</time></span>
              </button>
            ))}
            {!loading && !queue.items.length && <div className="queue-empty"><strong>No tickets in this view</strong><span>Only tickets from the currently published Jira project appear here.</span></div>}
            {loading && <div className="queue-empty"><span>Loading queue…</span></div>}
          </div>
        </section>

        <section className="panel review-editor-panel">
          {selectedTicket ? <>
            <header className="queue-detail-header">
              <div><div className="queue-detail-eyebrow"><span className={`queue-state ${statusTone(selectedTicket)}`}>{STATUS_LABELS[selectedTicket.review_status] || 'Needs review'}</span><span>{selectedTicket.ticket_key}</span></div><h2>{selectedTicket.summary || 'No summary provided'}</h2></div>
              {selectedTicket.source_url && <a className="secondary-button" href={selectedTicket.source_url} target="_blank" rel="noreferrer">Open Jira</a>}
            </header>

            <dl className="queue-metadata"><div><dt>Confidence</dt><dd><ConfidenceBadge ticket={selectedTicket} /></dd></div><div><dt>Draft</dt><dd><span className={`draft-state-tag ${Number(selectedTicket.response_revision || 1) > 1 ? 'amended' : ''}`}>{draftLabel(selectedTicket)}</span></dd></div><div><dt>Changed</dt><dd>{selectedTicket.change_percentage == null ? 'Not amended' : `${selectedTicket.change_percentage}%`}</dd></div></dl>
            {selectedTicket.confidence_explanation && <div className="queue-confidence-explanation">{selectedTicket.confidence_explanation}</div>}

            <section className="ticket-timing" aria-label="Ticket time breakdown">
              <div className="ticket-timing-heading"><div><h3>Time breakdown</h3><p>{selectedTicket.timing?.complete ? 'Ticket created to resolution' : 'Elapsed since the ticket was created'}</p></div><strong>{formatDuration(selectedTicket.timing?.total_seconds)}</strong></div>
              {selectedTicket.timing?.measured ? <>
                <div className="ticket-timing-bar" aria-hidden="true">
                  {[
                    ['dana', selectedTicket.timing.dana_seconds],
                    ['customer', selectedTicket.timing.customer_wait_seconds],
                    ['workflow', selectedTicket.timing.other_workflow_seconds],
                  ].filter(([, seconds]) => Number(seconds) > 0).map(([type, seconds]) => <span key={type} className={type} style={{ flexGrow: Math.max(Number(seconds), 1) }} />)}
                </div>
                <div className="ticket-timing-legend">
                  <span><i className="dana" />Dana processing <strong>{formatDuration(selectedTicket.timing.dana_seconds)}</strong></span>
                  <span><i className="customer" />Waiting for customer <strong>{formatDuration(selectedTicket.timing.customer_wait_seconds)}</strong></span>
                  <span><i className="workflow" />Other workflow time <strong>{formatDuration(selectedTicket.timing.other_workflow_seconds)}</strong></span>
                </div>
                <div className="ticket-first-response">First meaningful response: <strong>{formatDuration(selectedTicket.timing.first_response_seconds)}</strong></div>
              </> : <p className="timing-unavailable">Timing starts with tickets received after KPI tracking is enabled. Existing tickets remain visible but are not treated as measured samples.</p>}
            </section>

            {selectedTicket.delivery_error && <div className="error-banner editor-alert">Jira did not confirm delivery. No automatic retry will occur.</div>}

            <div className="response-editor">
              <div className="section-heading"><div><h3>Customer response</h3><p>Edit Dana’s response before making a review decision.</p></div><span className={dirty ? 'unsaved-label' : 'saved-label'}>{dirty ? 'Unsaved edits' : 'Saved'}</span></div>
              <textarea value={editor} onChange={event => setEditor(event.target.value)} disabled={completed || Boolean(busy)} aria-label="Customer response" />
            </div>

            {!completed && <section className="draft-feedback-panel" aria-labelledby="draft-feedback-heading">
              <div className="draft-feedback-heading"><div><h3 id="draft-feedback-heading">How would you rate Dana’s draft?</h3><p>Required before completing this review. Text changes are measured separately.</p></div><span className={feedbackChoice ? 'feedback-complete' : 'feedback-required'}>{feedbackChoice ? 'Feedback selected' : 'Required'}</span></div>
              <div className="feedback-options" role="radiogroup" aria-label="Dana draft feedback">
                <label className={`feedback-option accepted ${feedbackChoice === 'accepted' ? 'selected' : ''}`}><input type="radio" name="draft-feedback" value="accepted" checked={feedbackChoice === 'accepted'} onChange={() => { setFeedbackChoice('accepted'); setRejectionReasons([]); setRejectionDetail(''); setError(''); }} disabled={Boolean(busy)} /><span><strong>Accepted</strong><small>Dana’s draft was useful enough to send, with or without your edits.</small></span></label>
                <label className={`feedback-option rejected ${feedbackChoice === 'rejected' ? 'selected' : ''}`}><input type="radio" name="draft-feedback" value="rejected" checked={feedbackChoice === 'rejected'} onChange={() => { setFeedbackChoice('rejected'); setError(''); }} disabled={Boolean(busy)} /><span><strong>Rejected</strong><small>Dana’s draft was not usable. Nothing will be sent.</small></span></label>
              </div>
              {feedbackChoice === 'rejected' && <div className="feedback-detail"><fieldset><legend>Why was the draft rejected?</legend><div className="rejection-reason-grid">{REJECTION_OPTIONS.map(([value, label]) => <label key={value}><input type="checkbox" checked={rejectionReasons.includes(value)} onChange={() => setRejectionReasons(current => current.includes(value) ? current.filter(reason => reason !== value) : [...current, value])} disabled={Boolean(busy)} />{label}</label>)}</div></fieldset><label htmlFor="rejection-detail">Additional detail{rejectionReasons.some(reason => DETAIL_REQUIRED_REASONS.has(reason)) ? ' (required)' : ' (optional)'}</label><textarea id="rejection-detail" value={rejectionDetail} onChange={event => setRejectionDetail(event.target.value)} placeholder="Add useful context for Dana’s evaluation record" disabled={Boolean(busy)} /></div>}
            </section>}

            {!completed && <footer className="review-actions">
              <button className="secondary-button" type="button" onClick={() => setEditor(selectedTicket.original_draft_response || '')} disabled={Boolean(busy) || !changedFromDana}>Reset to Dana</button>
              <button className="secondary-button" type="button" onClick={saveDraft} disabled={Boolean(busy) || !dirty || !canonical(editor)}>{busy === 'save' ? 'Saving…' : 'Save draft'}</button>
              <button className={feedbackChoice === 'rejected' ? 'danger-button action-primary' : 'primary-button action-primary'} type="button" onClick={submitFeedback} disabled={Boolean(busy) || !feedbackChoice || !canonical(editor) || (feedbackChoice === 'rejected' && (!rejectionReasons.length || (rejectionReasons.some(reason => DETAIL_REQUIRED_REASONS.has(reason)) && canonical(rejectionDetail).length < 3)))}>{busy === 'send' ? 'Sending…' : busy === 'reject' ? 'Recording…' : feedbackChoice === 'accepted' ? (changedFromDana ? 'Accept and send edited response' : 'Accept and send') : feedbackChoice === 'rejected' ? 'Record rejection' : 'Select feedback to continue'}</button>
            </footer>}
          </> : <div className="queue-empty queue-detail-empty"><strong>Select a ticket to begin</strong><span>Ticket context and Dana’s response will appear here.</span></div>}
        </section>

        <aside className="panel review-context-panel">
          {selectedTicket ? <>
            <div className="context-section"><h2>Ticket context</h2><dl className="context-list"><div><dt>Project</dt><dd>{queue.project?.key || '—'}</dd></div><div><dt>Delivery</dt><dd>{selectedTicket.delivery_status === 'sent' ? 'Sent' : selectedTicket.delivery_status === 'failed' ? 'Failed' : 'Horizontl draft'}</dd></div><div><dt>Updated</dt><dd>{formatDateTime(selectedTicket.updated_at)}</dd></div></dl></div>

            {selectedTicket.attachments_present && <div className="attachment-notice"><strong>{selectedTicket.attachment_count} Jira attachment{selectedTicket.attachment_count === 1 ? '' : 's'}</strong><span>Files remain in Jira and were not stored or read by Horizontl. Review them before sending.</span><a href={selectedTicket.source_url} target="_blank" rel="noreferrer">Review in Jira</a></div>}

            <div className="context-section"><h3>Why confidence is {selectedTicket.confidence_score == null ? 'not scored' : `${selectedTicket.confidence_score}%`}</h3>{selectedTicket.confidence_explanation && <p className="context-muted">{selectedTicket.confidence_explanation}</p>}{selectedTicket.confidence_factors?.length ? <ul className="confidence-factor-list">{selectedTicket.confidence_factors.map(factor => <li key={factor}>{factor}</li>)}</ul> : <p className="context-muted">Detailed factors were not recorded for this earlier ticket.</p>}</div>

            <div className="context-section"><h3>Supporting evidence</h3><div className="queue-evidence-grid"><article><span>Resolution proposed</span><strong>{selectedTicket.resolution_found ? 'Yes' : 'No'}</strong></article><article><span>Documentation</span><strong>{selectedTicket.doc_feedback ? 'Gap identified' : 'No gap recorded'}</strong></article></div>{selectedTicket.doc_feedback && <div className="context-copy warning-copy">{selectedTicket.doc_feedback}</div>}</div>

            {selectedTicket.feedback_outcome === 'rejected' && <div className="context-section"><h3>Rejection feedback</h3><ul className="confidence-factor-list">{(selectedTicket.feedback_reason_codes || []).map(code => <li key={code}>{REJECTION_OPTIONS.find(([value]) => value === code)?.[1] || code}</li>)}</ul>{selectedTicket.feedback_reason_detail && <div className="context-copy">{selectedTicket.feedback_reason_detail}</div>}</div>}

            <details className="original-response"><summary>Original Dana response</summary><div className="context-copy">{selectedTicket.original_draft_response || 'Not available'}</div></details>
            <Link className="audit-link" to={`/tickets?highlight=${encodeURIComponent(selectedTicket.id)}`}>View audit record</Link>
          </> : <div className="queue-empty"><span>No ticket selected.</span></div>}
        </aside>
      </div>
    </div>
  );
}
